import { Injectable, inject, signal } from '@angular/core';
import { DataService } from './data.service';
import type { RequestType } from '../../enums/request-type.enum';
import type { Draft, DraftCounts, DraftSaveResult, DraftSummary, SaveDraftRequest } from '../models/draft';

const NO_DRAFTS: DraftCounts = { payment: 0, expectFunds: 0, transferFund: 0, journal: 0 };

/**
 * Saves, lists, reopens and deletes the current user's drafts, and keeps the draft counts
 * shown in the Drafts tab labels up to date.
 */
@Injectable({
    providedIn: 'root'
})
export class DraftsService {
    private readonly dataService = inject(DataService);
    private readonly countsState = signal<DraftCounts>(NO_DRAFTS);

    /** The current user's draft counts per request type. */
    readonly counts = this.countsState.asReadonly();

    /**
     * Lists the current user's non-expired drafts of one request type, most recently saved first.
     * @param requestType The request type to list.
     */
    list(requestType: RequestType): Promise<DraftSummary[]> {
        return this.dataService.get<DraftSummary[]>(`api/drafts?requestType=${requestType}`);
    }

    /**
     * Gets one draft with its saved form snapshot and documents.
     * @param id The Draft ID.
     */
    get(id: number): Promise<Draft> {
        return this.dataService.get<Draft>(`api/drafts/${id}`);
    }

    /**
     * Creates a draft. The content may be incomplete.
     * @param request The draft content.
     */
    async create(request: SaveDraftRequest): Promise<DraftSaveResult> {
        const result = await this.dataService.post<DraftSaveResult>('api/drafts', request);
        void this.refreshCounts();
        return result;
    }

    /**
     * Updates a draft. The request must carry the `rowVersion` from the last load or save;
     * the server answers 409 if the draft was changed or removed elsewhere in the meantime.
     * @param id The Draft ID.
     * @param request The new draft content.
     */
    update(id: number, request: SaveDraftRequest): Promise<DraftSaveResult> {
        return this.dataService.put<DraftSaveResult>('api/drafts', request, id);
    }

    /**
     * Permanently deletes a draft together with its documents.
     * @param id The Draft ID.
     */
    async delete(id: number): Promise<void> {
        await this.dataService.delete<void>('api/drafts', id);
        void this.refreshCounts();
    }

    /**
     * Reloads the draft counts. Call after anything that adds or removes a draft, such as submitting one.
     * The counts are a convenience, so a failure leaves the previous values in place.
     */
    async refreshCounts(): Promise<void> {
        try {
            this.countsState.set(await this.dataService.get<DraftCounts>('api/drafts/counts'));
        } catch {
            // DataService has already logged the failure; keep showing the last known counts.
        }
    }
}
